import classNames from "classnames";
import React, { useRef } from "react";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import iconLock from "../../../../img/checkout/lock.png";
import { Link } from "../../../components/Link";
import { FormCheckbox } from "../../../forms/FormCheckbox";
import { FormInput } from "../../../forms/FormInput";
import { FormNumber } from "../../../forms/FormNumber";
import { FormSubmit } from "../../../forms/FormSubmit";
import { strings } from "../../../localization";
import {
    IAddress,
    IAddressCountry,
    IAddressState,
} from "../../../models/address";
import {
    FinancingAccountInquiry,
    FinancingPlan,
} from "../../../models/financing";
import { IMonths, isoAPR, isoMonths } from "../../../models/nominals";
import { format, formatTermLength } from "../../../utils/format";
import { isElement } from "../../../utils/guards";
import { bindPrintPopupButtons } from "../../../utils/printPopup";
import { usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { FinancingModalTrigger } from "../../financing/FinancingModalTrigger";
import { IFormErrors } from "../PaymentMethod.interfaces";
import { FinePrint } from "../PaymentMethodFinancingFinePrint";
import { RequestLimitIncrease } from "../PaymentMethodFinancingRequestLimitIncrease";
import { SelectPlanForm } from "../PaymentMethodFinancingSelectPlanForm";
import { IPayment } from "../reducers.interfaces";
import {
    IFinancingPayment,
    IReduxDerivedData,
    IReduxFormState,
} from "../reducers.interfaces";
import { isFortivaAcctNum } from "../utils";

import fortivaFinancingStyles from "./index.module.scss";

interface IProps {
    methodKey: string;
    countries: readonly IAddressCountry[];
    billing_states: readonly IAddressState[];
    savedAddresses: readonly IAddress[];
    form: IReduxFormState;
    errors: {
        [method_key: string]: IFormErrors;
    };
    showErrorMessages: boolean;
    setPaymentMethodFields: (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => void;
    changeFinancingAccountNumber: (
        methodKey: string,
        accountNumber: string,
    ) => void;
    changeAmount: (methodKey: string, amount: string) => void;
    onValidStateChange: (field: string, errors: string[]) => void;
    plans: readonly FinancingPlan[];
    grandTotal: string;
    allowCreditApplications: boolean;
    switchToCreditCard: () => void;
    newAccount?: FinancingAccountInquiry;
    basketTotal?: number;
    cartID: string;
    header: string | null;
    isSplitPay: boolean;
    shouldFreezeAmount: boolean;
    disabled: boolean;
    derived: IReduxDerivedData;
    addSplitPayToFinancing: () => void;
    enableSplitPay: boolean;
}

type TPrintTypes = "welcome" | "terms";

const getMethodData = (
    form: IReduxFormState,
    methodKey: string,
): IFinancingPayment => {
    const data = form.payment_methods[methodKey];
    let wellsFargoData: IFinancingPayment | null = null;
    if (data.method_type === "financing") {
        wellsFargoData = data;
    }
    const baseFields: IFinancingPayment = {
        method_type: "financing",
        amount: "",
        pay_balance: false,
        new_financing_account: null,
        financing_account: "",
        financing_plan: null,
        has_agreed: false,
        has_esigned: false,
    };
    return {
        ...baseFields,
        ...wellsFargoData,
    };
};

const handleAccordionClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
        event.preventDefault();
    }
    const document = getDocument();
    if (!document) {
        return;
    }

    const targetElem = event.currentTarget;
    if (targetElem.classList.contains("accordion--is-active")) {
        targetElem.classList.remove("accordion--is-active");
        if (targetElem.nextElementSibling) {
            targetElem.nextElementSibling.classList.remove(
                "accordion-target--is-active",
            );
        }
    } else {
        const accordionElems = document.getElementsByClassName(
            "accordion--is-active",
        );
        const accordionTargetElems = document.getElementsByClassName(
            "accordion-target--is-active",
        );
        while (accordionElems.length > 0) {
            accordionElems[0].classList.remove("accordion--is-active");
        }
        while (accordionTargetElems.length > 0) {
            accordionTargetElems[0].classList.remove(
                "accordion-target--is-active",
            );
        }
        targetElem.classList.add("accordion--is-active");
        if (targetElem.nextElementSibling) {
            targetElem.nextElementSibling.classList.add(
                "accordion-target--is-active",
            );
        }
    }
    const anchor = document.getElementById(
        "financing-new-account__accordion-anchor",
    );
    if (anchor) {
        anchor.scrollIntoView();
    }
};

const handleAccountInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
        event.preventDefault();
    }
    const document = getDocument();
    if (!document) {
        return;
    }

    const anchor = document.getElementById("financing-new-account__anchor");
    if (anchor) {
        anchor.scrollIntoView();
    }
};

const openPrintWindow = (
    printType: TPrintTypes,
    welcomeElem: HTMLElement | null,
    termsElem: HTMLElement | null,
) => {
    const window = getWindow();
    const document = getDocument();
    if (!window || !document) {
        return;
    }

    const messageElems: { [t in TPrintTypes]: HTMLElement | null } = {
        welcome: welcomeElem,
        terms: termsElem,
    };
    const messageElem = messageElems[printType];
    if (!messageElem) {
        return;
    }
    const width = 640;
    const height = 480;
    const options = [
        ["toolbar", "no"],
        ["location", "no"],
        ["directories", "no"],
        ["status", "no"],
        ["menubar", "no"],
        ["scrollbars", "yes"],
        ["resizable", "yes"],
        ["width", width],
        ["height", height],
        ["top", screen.height / 2 - height / 2],
        ["left", screen.width / 2 - width / 2],
    ];
    const encodedOptions = options
        .map((pair) => {
            return pair.join("=");
        })
        .join(", ");
    const popupTitles: { [t in TPrintTypes]: string } = {
        welcome: t`Wells Fargo Account Details`,
        terms: t`Wells Fargo Terms and Conditions`,
    };
    const popup = window.open("", popupTitles[printType], encodedOptions);
    if (!popup) {
        return;
    }
    const messageText = isElement(messageElem)
        ? messageElem.innerHTML
        : messageElem;
    if (document.head && popup.document.head) {
        popup.document.head.innerHTML = document.head.innerHTML;
    }
    popup.document.body.innerHTML = `
            <div class="financing-form__welcome" style="margin: 20px;">
                ${messageText}
                <button class="button no-print" data-popup-action="print">Print</button>
                <button class="button no-print" data-popup-action="close">Close</button>
            </div>
        `;
    bindPrintPopupButtons(popup);
    popup.print();
};

const buildMinFinancingAmount = (plans: number[], isFortiva: boolean) => {
    if (isFortiva) {
        return 0;
    }
    return plans.length ? Math.min(...plans) : 0;
};

export const AddAccountForm = (props: IProps): React.ReactNode => {
    const currentPlanMonthsRef = useRef(0);
    const termsElemRef = useRef<HTMLElement | null>(null);
    const welcomeElemRef = useRef<HTMLElement | null>(null);
    const financePageURL = usePageURL("finance_link");
    const homePageURL = usePageURL("home");
    const financingDefaultPlan = usePageSetting("financing_default_plan");

    const onPrintWelcome = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (event) {
            event.preventDefault();
        }
        openPrintWindow(
            "welcome",
            welcomeElemRef.current,
            termsElemRef.current,
        );
    };

    const onPrintTerms = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (event) {
            event.preventDefault();
        }
        openPrintWindow("terms", welcomeElemRef.current, termsElemRef.current);
    };

    const onSetPlanMonths = (months: IMonths) => {
        currentPlanMonthsRef.current = isoMonths.unwrap(months);
    };

    const onAcctNumChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeFinancingAccountNumber(
            props.methodKey,
            event.currentTarget.value,
        );
    };

    const onCheckboxChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.setPaymentMethodFields(props.methodKey, {
            [event.currentTarget.name]: event.currentTarget.checked,
        });
    };

    const onAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
        const amount = event.currentTarget.value;
        props.changeAmount(props.methodKey, amount);

        // check if amount is lower than plan threshold
        const paymentMethod = props.form.payment_methods[
            props.methodKey
        ] as IFinancingPayment;
        if (paymentMethod.financing_plan) {
            const amountFloat = parseFloat(amount.replace(/[^0-9.]+/g, ""));
            const currentPlan = props.plans.find((plan) => {
                return plan.id === paymentMethod.financing_plan;
            });
            if (
                currentPlan &&
                amountFloat < parseFloat(currentPlan.product_price_threshold)
            ) {
                // try to find a plan with lower threshold
                let planId = currentPlan.id;
                for (const plan of props.plans) {
                    if (
                        parseFloat(plan.product_price_threshold) <= amountFloat
                    ) {
                        planId = plan.id;
                    }
                }
                // update to the lesser plan if one was found
                if (planId !== currentPlan.id) {
                    props.setPaymentMethodFields(props.methodKey, {
                        financing_plan: planId,
                    });
                }
            }
        }
    };

    const onSplitPay = (event: React.MouseEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }
        const document = getDocument();
        if (!document) {
            return;
        }

        const anchor = document.getElementById("financing-header__anchor");
        if (anchor) {
            anchor.scrollIntoView();
        }
        props.addSplitPayToFinancing();
    };

    const onPrintScroll = () => {
        const window = getWindow();
        if (!window) {
            return;
        }

        if (window.pageYOffset.toFixed() === "0") {
            window.removeEventListener("scroll", onPrintScroll);
            // TODO: Determine why setTimeout is necessary. Seems to be a race condition between the eventListener and the actual scrolling
            setTimeout(() => {
                window.print();
            }, 1);
        }
    };

    const buildApplyBlock = () => {
        const bestPlan = props.plans
            .filter((plan) => {
                return parseFloat(isoAPR.unwrap(plan.apr)) <= 0;
            })
            .reduce<FinancingPlan | null>((memo, plan) => {
                return memo && memo.term_months >= plan.term_months
                    ? memo
                    : plan;
            }, null);
        let qualificationMsg: React.JSX.Element | undefined;
        if (bestPlan) {
            qualificationMsg = (
                <li className="checkout-step-apply-block__list-item">
                    Your order qualifies for 0% APR for{" "}
                    {formatTermLength(bestPlan.term_months)} months
                    <sup>{bestPlan.fine_print_superscript}</sup>
                </li>
            );
        } else {
            if (financingDefaultPlan) {
                qualificationMsg = (
                    <li className="checkout-step-apply-block__list-item">
                        {interpolate(
                            t`Orders of %(defaultThreshold)s or more qualify for %(apr)s APR for %(termMonths)s months`,
                            {
                                defaultThreshold: `${format.money(
                                    financingDefaultPlan.product_price_threshold,
                                )}`,
                                apr: `${parseInt(financingDefaultPlan.apr || "0", 10)}`,
                                termMonths: `${financingDefaultPlan.term_months}`,
                            },
                        )}
                        <sup>{financingDefaultPlan.fine_print_superscript}</sup>
                    </li>
                );
            }
        }
        return (
            <div className="checkout-step-apply-block">
                <div className="checkout-step-apply-block__header">
                    <div className="checkout-step-apply-block__header-block">
                        <h4 className="checkout-step-apply-block__copy checkout-step-apply-block__copy--bold">
                            {strings.get("FINANCING_APPLY_CTA_HEAD")}
                        </h4>
                        <div className="checkout-step-apply-block__copy">
                            {strings.get("FINANCING_APPLY_CTA_SUBHEAD")}
                        </div>
                        <a
                            className="checkout-step-apply-block__link checkout-step-apply-block__link--desktop"
                            href="#consent-form"
                        >
                            Financing Terms
                        </a>
                    </div>
                    <div className="checkout-step-apply-block__header-block">
                        <ul className="checkout-step-apply-block__list">
                            <li className="checkout-step-apply-block__list-item">
                                No annual fee<sup>1</sup>
                            </li>
                            <li className="checkout-step-apply-block__list-item">
                                Quick and secure online application
                            </li>
                            <li className="checkout-step-apply-block__list-item">
                                Exclusive Special Financing Offers
                            </li>
                            {qualificationMsg}
                        </ul>
                        <Link
                            className="checkout-step-apply-block__link checkout-step-apply-block__link--mobile"
                            href={financePageURL || homePageURL}
                            target="_top"
                        >
                            Financing Terms
                        </Link>
                    </div>
                </div>
                <div className="checkout-step-apply-block__button-container">
                    <FinancingModalTrigger
                        className="button financing-form__button al-financing-form__apply-now-button"
                        modalType="full-app"
                    >
                        {t`Apply Now`}
                    </FinancingModalTrigger>
                </div>
            </div>
        );
    };

    const buildCreditAppHeader = () => {
        const methodData = getMethodData(props.form, props.methodKey);
        return (
            <div
                className="financing-form__terms"
                tabIndex={0}
                id="consent-form"
            >
                <div className="financing-form__terms-header">
                    <h4 className="financing-form__terms-title checkout-step__copy--bold">
                        {strings.get("FINANCING_CONSENT_DELIVERY_HEAD")}
                    </h4>
                    <a
                        href="#print"
                        className="financing-form__terms-print"
                        onClick={() => {
                            const window = getWindow();
                            if (window) {
                                window.addEventListener(
                                    "scroll",
                                    onPrintScroll,
                                );
                                window.scrollTo(0, 0);
                            }
                        }}
                        title={`Print ${strings.get("FINANCING_CONSENT_BODY")}`}
                    >
                        print
                    </a>
                </div>
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html:
                                strings.get(
                                    "FINANCING_CONSENT_DELIVERY_BODY",
                                ) || "",
                        }}
                    ></p>
                    <p
                        className="financing-form__terms-copy"
                        ref={(elem) => {
                            termsElemRef.current = elem;
                        }}
                        dangerouslySetInnerHTML={{
                            __html: strings.get("FINANCING_CONSENT_BODY") || "",
                        }}
                    ></p>
                </div>
                <div>
                    <a
                        href="#print"
                        className="checkout-step__copy--underline"
                        onClick={onPrintTerms}
                        title={`Print ${strings.get("FINANCING_CONSENT_BODY")}`}
                    >
                        <h4 className="checkout-step__copy--bold">
                            {strings.get("FINANCING_CONSENT_ESIGN_HEAD")}
                        </h4>
                    </a>
                    <div className={fortivaFinancingStyles.checkbox}>
                        <FormCheckbox
                            label={t`I consent to the terms and conditions of ESIGN Consent to receive documents electronically. I am able to receive and view email and PDF files.`}
                            name="has_agreed"
                            checked={methodData.has_agreed}
                            onChange={onCheckboxChange}
                            disabled={props.disabled}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const methodData = getMethodData(props.form, props.methodKey);

    const basketTotal = props.basketTotal || 0;
    const isTotalCovered =
        props.newAccount &&
        parseFloat(props.newAccount.credit_limit) >= basketTotal;
    let totalDifference: string | null = null;
    if (props.newAccount) {
        totalDifference = isTotalCovered
            ? "0.00"
            : (
                  Math.round(
                      1000 *
                          (basketTotal -
                              parseFloat(props.newAccount.credit_limit)),
                  ) / 1000
              ).toFixed(2);
    }

    let header: React.JSX.Element | null = null;
    if (props.allowCreditApplications) {
        header = buildCreditAppHeader();
    }

    const submitClasses = classNames({
        "button": true,
        "button--full-width": true,
        "button--place-order": true,
        [`checkout-place-order-button--wf-${currentPlanMonthsRef.current}`]: true,
        [`al-checkout__place-order-button--wf-${currentPlanMonthsRef.current}`]: true,
    });

    const errors =
        props.methodKey in props.errors ? props.errors[props.methodKey] : {};

    const plansAboveZero = props.plans
        .map((plan) => parseFloat(plan.product_price_threshold))
        .filter((amount) => amount > 0);
    const minFinancingAmount = buildMinFinancingAmount(
        plansAboveZero,
        isFortivaAcctNum(methodData.financing_account),
    );

    return (
        <div>
            <h4
                className="checkout-step__bar-heading"
                id="financing-header__anchor"
            >
                {props.header}
            </h4>
            {props.newAccount ? (
                <div>
                    <div
                        className="financing-new-account--inline"
                        id="financing-new-account__anchor"
                    >
                        <div
                            className="financing-new-account__header"
                            ref={(elem) => {
                                welcomeElemRef.current = elem;
                            }}
                        >
                            <div className="financing-new-account__header-copy">
                                <span className="financing-new-account__header-copy--bold">
                                    {t`Congratulations, you're approved!`}
                                </span>
                            </div>
                            <div className="financing-new-account__header-details">
                                <div className="financing-new-account__header-number">
                                    <div className="financing-new-account__header-number--title">
                                        {t`Your account number:`}
                                    </div>
                                    <span className="financing-new-account__header-number--bold">
                                        {format.wellsFargoAccountNumber(
                                            methodData.financing_account,
                                        )}
                                    </span>
                                </div>
                                <div className="financing-new-account__header-number">
                                    <div className="financing-new-account__header-number--title">
                                        {t`Your approved credit:`}
                                    </div>
                                    <span className="financing-new-account__header-number--bold">
                                        {format.money(
                                            props.newAccount.credit_limit,
                                        )}
                                    </span>
                                </div>
                                <a
                                    href="#print"
                                    title={t`Print Special Terms and APR Information`}
                                    className="financing-new-account__print"
                                    onClick={onPrintWelcome}
                                >
                                    print
                                </a>
                            </div>
                        </div>
                    </div>
                    {!isTotalCovered ? (
                        <div className="financing-new-account__deficit">
                            <div
                                className="financing-new-account__warning"
                                id="financing-new-account__accordion-anchor"
                            >
                                <div className="financing-new-account__warning-icon-container">
                                    <div className="financing-new-account__warning-icon">
                                        !
                                    </div>
                                </div>
                                <div className="financing-new-account__warning-copy">
                                    <div>
                                        <span className="financing-new-account__warning-copy--bold">
                                            {t`Order total exceeds your available credit limit.`}
                                        </span>
                                        <br />
                                        {interpolate(
                                            t`Your order total exceeds your credit limit by %s. View your options below.`,
                                            [
                                                `${format.money(
                                                    totalDifference,
                                                )}`,
                                            ],
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="financing-new-account__deficit-options accordion__container">
                                {props.enableSplitPay ? (
                                    <div>
                                        <div>
                                            {t`Choose an option to complete your order:`}
                                        </div>
                                        <ul className="accordion__list accordion__list--checkout-finance">
                                            <li className="accordion__list-item accordion__list-item--checkout-finance">
                                                <a
                                                    className="accordion accordion--checkout-finance accordion--arrow"
                                                    onClick={
                                                        handleAccordionClick
                                                    }
                                                >
                                                    {t`Use multiple payment methods`}
                                                </a>
                                                <div className="accordion-target accordion-target--checkout-finance">
                                                    <span className="financing-new-account__arrow-down"></span>
                                                    <span className="financing-new-account__sub-header-copy--bold">
                                                        {t`Multiple Payment Methods`}
                                                    </span>
                                                    <div>
                                                        {interpolate(
                                                            t`Utilize your approved credit limit of %(creditLimit)s and pay the difference of %(difference)s with a major credit card. To do this, we'll take you to our split pay option to enter your additional payment details.`,
                                                            {
                                                                creditLimit: `${format.money(
                                                                    props
                                                                        .newAccount
                                                                        .credit_limit,
                                                                )}`,
                                                                difference: `${format.money(
                                                                    totalDifference,
                                                                )}`,
                                                            },
                                                        )}
                                                    </div>
                                                    <div className="financing-new-account__warning">
                                                        <div className="financing-new-account__warning-icon-container">
                                                            <div className="financing-new-account__warning-icon">
                                                                !
                                                            </div>
                                                        </div>
                                                        <div className="financing-new-account__warning-copy">
                                                            <div>
                                                                <span className="financing-new-account__warning-copy--bold">
                                                                    {t`Your privacy is important to us`}
                                                                </span>
                                                                <br />
                                                                {richT(
                                                                    t`For security reasons we don't store your account number. Please take note of or print your <AccountInfoClick>account information</AccountInfoClick> now. You will not have access to it again until you receive your card in the mail.`,
                                                                    {
                                                                        AccountInfoClick:
                                                                            (
                                                                                children,
                                                                            ) => (
                                                                                <a
                                                                                    onClick={
                                                                                        handleAccountInfoClick
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        children
                                                                                    }
                                                                                </a>
                                                                            ),
                                                                    },
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <a
                                                            className="button button--full-width"
                                                            onClick={onSplitPay}
                                                        >
                                                            {t`Go to Split Pay`}
                                                        </a>
                                                    </div>
                                                </div>
                                            </li>
                                            <li className="accordion__list-item accordion__list-item--checkout-finance">
                                                <a
                                                    className="accordion accordion--checkout-finance accordion--arrow"
                                                    onClick={
                                                        handleAccordionClick
                                                    }
                                                >
                                                    {t`Request a credit limit increase`}
                                                </a>
                                                <RequestLimitIncrease
                                                    methodKey={props.methodKey}
                                                    form={props.form}
                                                    plans={props.plans}
                                                    grandTotal={
                                                        props.grandTotal
                                                    }
                                                    setPaymentMethodFields={
                                                        props.setPaymentMethodFields
                                                    }
                                                    disabled={props.disabled}
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="accordion__list-item accordion__list-item--checkout-finance">
                                        <a
                                            className="accordion accordion--checkout-finance accordion--arrow"
                                            onClick={handleAccordionClick}
                                        >
                                            {t`Request a credit limit increase`}
                                        </a>
                                        <RequestLimitIncrease
                                            methodKey={props.methodKey}
                                            form={props.form}
                                            plans={props.plans}
                                            grandTotal={props.grandTotal}
                                            setPaymentMethodFields={
                                                props.setPaymentMethodFields
                                            }
                                            disabled={props.disabled}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <span>
                            <div className="financing-form__plan-select financing-new-account__plan-select">
                                <SelectPlanForm
                                    methodKey={props.methodKey}
                                    form={props.form}
                                    plans={props.plans}
                                    grandTotal={props.grandTotal}
                                    setPlanMonths={onSetPlanMonths}
                                    setPaymentMethodFields={
                                        props.setPaymentMethodFields
                                    }
                                    disabled={props.disabled}
                                />
                            </div>
                            <FormSubmit
                                value={t`Place Order`}
                                className={submitClasses}
                            />
                            <FinePrint />
                        </span>
                    )}
                </div>
            ) : (
                <>
                    <div className="checkout-step__form-container checkout-step__form-container--financing checkout-step__form-container--blue">
                        <fieldset
                            disabled={props.disabled}
                            style={{ border: 0 }}
                        >
                            <legend className="ada-screenreader-only">
                                Account Information
                            </legend>
                            <h3>
                                <label
                                    className="checkout-step__copy--bold"
                                    htmlFor="financing_account"
                                >
                                    Account Number
                                </label>
                            </h3>
                            <FormInput
                                name="financing_account"
                                id="financing_account"
                                type="text"
                                autoComplete="section-financing cc-number"
                                validation={["required", "financing-account"]}
                                value={format.wellsFargoAccountNumber(
                                    methodData.financing_account,
                                )}
                                errors={errors.financing_account}
                                onValidStateChange={props.onValidStateChange}
                                showErrorMessages={props.showErrorMessages}
                                onChange={onAcctNumChange}
                                maxLength={19}
                                inputMode="numeric"
                                secureImage={{
                                    image: iconLock,
                                    alt: t`Secure`,
                                }}
                            />
                            {props.isSplitPay && (
                                <div>
                                    <label
                                        className="checkout-step__copy--bold"
                                        htmlFor="financing-amount"
                                    >
                                        Amount
                                    </label>
                                    <FormNumber
                                        name="amount"
                                        id="financing-amount"
                                        autoComplete=""
                                        step={0.01}
                                        validation={[
                                            "required",
                                            "amount",
                                            "nonzero",
                                        ]}
                                        showErrorMessages={true}
                                        onValidStateChange={
                                            props.onValidStateChange
                                        }
                                        value={methodData.amount || undefined}
                                        errors={errors.amount}
                                        onChange={onAmountChange}
                                        disabled={props.shouldFreezeAmount}
                                        min={minFinancingAmount}
                                    />
                                </div>
                            )}
                            {props.disabled && (
                                <div>
                                    <h5>{t`Pre-Authorized`}</h5>
                                    <p>
                                        {interpolate(
                                            t`This account has been pre-authorized for the amount of %s.`,
                                            [
                                                format.money(
                                                    methodData.amount || "0.00",
                                                ),
                                            ],
                                        )}
                                    </p>
                                </div>
                            )}
                            {!props.isSplitPay &&
                                !isFortivaAcctNum(
                                    methodData.financing_account,
                                ) &&
                                buildApplyBlock()}
                        </fieldset>
                        {!isFortivaAcctNum(methodData.financing_account) && (
                            <>
                                <div className="financing-form__plan-select">
                                    <SelectPlanForm
                                        methodKey={props.methodKey}
                                        form={props.form}
                                        plans={props.plans}
                                        grandTotal={props.grandTotal}
                                        setPlanMonths={onSetPlanMonths}
                                        setPaymentMethodFields={
                                            props.setPaymentMethodFields
                                        }
                                        disabled={props.disabled}
                                    />
                                </div>
                                {header}
                                <div className="financing-form__esign">
                                    <h4 className="checkout-step__copy--bold">
                                        Electronic Signature
                                    </h4>
                                    <div
                                        className={
                                            fortivaFinancingStyles.checkbox
                                        }
                                    >
                                        <FormCheckbox
                                            label={t`By checking this box, I acknowledge that this is my electronic signature and that this transaction is under my Credit Card Agreement with Wells Fargo Bank, N.A. I acknowledge receipt of the following documents at the time I opened my account:`}
                                            name="has_esigned"
                                            checked={methodData.has_esigned}
                                            onChange={onCheckboxChange}
                                            disabled={props.disabled}
                                        />
                                    </div>
                                    <ul>
                                        <li>Credit Card Agreement</li>
                                        <li>
                                            Important Terms of Your Credit Card
                                            Account
                                        </li>
                                        <li>
                                            Wells Fargo Retail Services Privacy
                                            Notice
                                        </li>
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                    {isFortivaAcctNum(methodData.financing_account) && (
                        <>
                            <p className={fortivaFinancingStyles.terms}>
                                {t`By clicking Place Order, the above transaction will be financed by your Fortiva® Retail Credit Account in accordance with the Terms and Conditions previously provided to you.`}
                            </p>
                        </>
                    )}
                </>
            )}
        </div>
    );
};
